
import { OrderStatus } from '@/types/orders';
import { Badge } from '@/components/ui/badge';
import {
  Clock,
  Utensils,
  CheckCircle,
  ShoppingBag,
  XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface OrderStatusBadgeProps {
  status: OrderStatus;
}

export function OrderStatusBadge({ status }: OrderStatusBadgeProps) {
  const { t } = useTranslation();

  const config = {
    pending: {
      variant: 'warning' as const,
      icon: Clock,
      label: t('pending'),
    },
    preparing: {
      variant: 'info' as const,
      icon: Utensils,
      label: t('preparing'),
    },
    ready: {
      variant: 'success' as const,
      icon: CheckCircle,
      label: t('ready'),
    },
    delivered: {
      variant: 'default' as const,
      icon: ShoppingBag,
      label: t('delivered'),
    },
    completed: {
      variant: 'success' as const,
      icon: CheckCircle,
      label: t('completed'),
    },
    cancelled: {
      variant: 'destructive' as const,
      icon: XCircle,
      label: t('cancelled'),
    },
  };

  const { variant, icon: Icon, label } = config[status];
  const getBadgeClasses = () => {
    switch (variant) {
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
      case 'info':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'success':
        return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'destructive':
        return 'bg-red-100 text-red-800 hover:bg-red-100';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  };

  return (
    <Badge variant='outline' className={getBadgeClasses()}>
      <Icon className='mr-1 h-3 w-3' />
      {label}
    </Badge>
  );
}
